import React, { useState, useEffect, useMemo, useRef } from 'react';
import API_BASE_URL from '../config';
import Lottie from 'lottie-react';
import loader from '../Components/css/loader.json'; // Path to your loader JSON file
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Dashboard/Header.js';
import { Box, Typography, Button, TextField, InputAdornment, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Collapse } from '@mui/material';
import { Search as SearchIcon, ArrowDownward, ArrowUpward, Sort, PictureAsPdf, TableView, Add, Remove } from '@mui/icons-material';
import { jsPDF } from 'jspdf';  // For PDF generation
import * as XLSX from 'xlsx';  // For Excel generation

const Stock = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState([]); // Store fetched data
  const [loading, setLoading] = useState(true); // Manage loading state
  const [error, setError] = useState(null); // Manage error state
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    itemsPerPage: 20,
  }); // Pagination state
  const [expandedRows, setExpandedRows] = useState({}); // Track expanded rows
  const [departmentFilter, setDepartmentFilter] = useState(''); // Department filter state
  const [searchQuery, setSearchQuery] = useState(''); // Search query state
  const [showDropdown, setShowDropdown] = useState(false); // State for showing file options dropdow
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'ascending' }); // Sorting state

  // Refs for handling click outside the dropdown
  const dropdownRef = useRef(null);  // Ref for the dropdown container
  const buttonRef = useRef(null);    // Ref for the generate file button

  // Extract query parameters from URL
  const queryParams = new URLSearchParams(location.search);
  const departmentCode = queryParams.get('departmentcode') || '';
  const groupCode = queryParams.get('groupcode') || '';
  const brandCode = queryParams.get('brandcode') || '';
  const designCode = queryParams.get('designcode') || '';
  const colorCode = queryParams.get('colorcode') || '';
  const sizeCode = queryParams.get('sizecode') || '';
  const patternCode = queryParams.get('patterncode') || '';
  const seasonCode = queryParams.get('seasoncode') || '';

  const grandTotal = useMemo(() => {
    return data.reduce((total, row) => total + (parseFloat(row['Grand Total']) || 0), 0);
  }, [data]);

  // Handle file generation (PDF and Excel)
  const handleGenerateFile = (fileType) => {
    if (fileType === 'PDF') {
      generatePDF();
    } else if (fileType === 'Excel') {
      generateExcel();
    }
    setShowDropdown(false); // Hide the dropdown after generating the file
  };


  const generatePDF = () => {
    const doc = new jsPDF('landscape'); // Create a landscape-oriented PDF

    // Add a bold title at the top of the PDF
    doc.setFont('helvetica', 'bold'); // Set font to bold
    doc.setFontSize(16);
    doc.text('Stock Report', doc.internal.pageSize.width / 2, 20, { align: 'center' });

    // Prepare table headers and data (include all headers)
    const header = getTableHeaders.map(header => header); // Include all headers (even if they are empty or '-')
    const rows = data.map(row => getTableHeaders.map(header => row[header] || '-')); // Ensure all data is included, even '-'

    // Set the font size and other table styles
    doc.autoTable({
      head: [header],
      body: rows,
      theme: 'grid',
      margin: { top: 30, bottom: 25 }, // Reduced bottom margin for more space
      startY: 30, // Start table a bit higher to utilize more space
      styles: {
        fontSize: 8,       // Smaller font size for compactness
        cellPadding: 1,    // Less padding for smaller rows
        halign: 'center',  // Horizontal alignment in cells
      },
      headStyles: {
        fillColor: [70, 130, 180],  // Light blue color (RGB)
        textColor: [255, 255, 255], // White text color for better contrast
        fontSize: 7,                // Slightly larger font for headers
      },
      didDrawPage: function (data) {
        // This function will be called after each page is drawn
        doc.setFontSize(8);
        doc.text(`Page ${data.pageNumber}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 10, { align: 'center' });
      },
    });

    // Save the PDF
    doc.save('stock_report.pdf');
  };

  const generateExcel = () => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock Report');
    XLSX.writeFile(wb, 'stock_report.xlsx');
  };

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      const userId = localStorage.getItem('userId');
      const accessToken = localStorage.getItem('accessToken');
      const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany'));

      if (!selectedCompany || !selectedCompany.CompCode) {
        setError('Company code is missing or invalid');
        setLoading(false);
        return;
      }

      const compCode = selectedCompany.CompCode;

      if (!userId || !accessToken) {
        setError('Missing userId or accessToken in localStorage');
        setLoading(false);
        return;
      }

      try {
        // Create an AbortController instance
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 90000); // Set timeout to 90 seconds

        const response = await fetch(
          `${API_BASE_URL}/api/createItemProcedure?CompCode=${compCode}&departmentcode=${departmentCode}&groupcode=${groupCode}&brandcode=${brandCode}&designcode=${designCode}&colorcode=${colorCode}&sizecode=${sizeCode}&patterncode=${patternCode}&seasoncode=${seasonCode}&page=${paginationInfo.currentPage}&limit=${paginationInfo.itemsPerPage}&search=${searchQuery}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'UserId': userId,
              'Authorization': `Bearer ${accessToken}`,
            },
            signal: controller.signal, // Attach the signal from the AbortController to the fetch request
          }
        );

        // Clear the timeout once the request is complete
        clearTimeout(timeoutId);

        const responseBody = await response.json();

        if (!response.ok) {
          throw new Error(`http error! Status: ${response.status}`);
        }

        if (!responseBody || responseBody.data.length === 0) {
          setError('No data available');
        } else {
          setData(responseBody.data); // Set fetched data to the state
        }
      } catch (error) {
        // Handle different types of errors separately
        if (error.name === 'AbortError') {
          console.error('Request timed out'); // Log timeout error
          setError('The request timed out. Please try again.');
        } else {
          console.error('Error fetching data:', error); // Log other errors
          setError('Error fetching data, please try again later.');
        }
      } finally {
        setLoading(false); // Make sure to stop the loading state
      }
    };

    fetchData();
  }, [departmentCode, groupCode, brandCode, designCode, colorCode, sizeCode, patternCode, seasonCode, paginationInfo.currentPage, paginationInfo.itemsPerPage, searchQuery]);

  // Memoize table headers (dynamically based on the first item in the data array)
  const getTableHeaders = useMemo(() => {
    if (data.length > 0) {
      const allHeaders = Object.keys(data[0]);
      const requiredHeaders = ['DEPARTMENT', 'GROUPNAME', 'BRAND', 'LEATHER', 'COLOR', 'PATTERN', 'SEASON', 'DESIGN', 'DESGIN', 'Grand Total'];
      const customColumns = requiredHeaders.filter(header => allHeaders.includes(header));
      return [...customColumns, ...allHeaders.filter(header => !customColumns.includes(header))];
    }
    return [];
  }, [data]);

  const mainRowHeaders = ['DEPARTMENT', 'GROUPNAME', 'BRAND', 'LEATHER', 'COLOR', 'PATTERN', 'SEASON', 'DESIGN', 'DESGIN', 'Grand Total'];

  // Get unique departments dynamically from the data
  const departmentOptions = useMemo(() => {
    const departments = data.map(item => item.DEPARTMENT);
    return [...new Set(departments)]; // Remove duplicates by using Set
  }, [data]);

  // Toggle row expansion
  const toggleRowExpansion = (rowIndex) => {
    setExpandedRows(prevState => ({
      ...prevState,
      [rowIndex]: !prevState[rowIndex], // Toggle the expansion state for the row
    }));
  };

  // Filter and Sort data based on department, search query, and sort config
  const filteredData = useMemo(() => {
    let filtered = [...data];

    // Filter by department
    if (departmentFilter) {
      filtered = filtered.filter(item => item.DEPARTMENT === departmentFilter);
    }

    // Filter by search query across selected fields
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter((item) =>
        ['GROUPNAME', 'BRAND', 'DESGIN', 'COLOR', 'PATTERN'].some((key) =>
          item[key] && item[key].toLowerCase().includes(searchLower)
        )
      );
    }

    // Sort data
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue === bValue) return 0;

        const comparison = (aValue < bValue) ? -1 : 1;
        return sortConfig.direction === 'ascending' ? comparison : -comparison;
      });
    }

    return filtered;
  }, [data, departmentFilter, searchQuery, sortConfig]);

  // Paginate the filtered data
  const totalPages = Math.ceil(filteredData.length / paginationInfo.itemsPerPage);

  // Get the data for the current page
  const currentPageData = useMemo(() => {
    const startIndex = (paginationInfo.currentPage - 1) * paginationInfo.itemsPerPage;
    const endIndex = startIndex + paginationInfo.itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, paginationInfo]);

  // Handle page change (Previous and Next)
  const handlePageChange = (direction) => {
    setPaginationInfo((prev) => {
      const newPage =
        direction === 'next' ? prev.currentPage + 1 : prev.currentPage - 1;
      return {
        ...prev,
        currentPage: Math.max(1, Math.min(newPage, totalPages)), // Ensure page is within bounds
      };
    });
  };

  // Handle department filter change
  const handleDepartmentChange = (event) => {
    setDepartmentFilter(event.target.value);
    setPaginationInfo({ currentPage: 1, itemsPerPage: 20 }); // Reset to first page
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPaginationInfo({ currentPage: 1, itemsPerPage: 20 }); // Reset to first page
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Close the dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        buttonRef.current && !buttonRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <Box className="flex h-screen overflow-hidden bg-slate-50 font-sans">
      <Box
        component="main"
        className="flex-1 flex flex-col h-full min-w-0 transition-all duration-300"
      >
        <Header />
        <Box className="flex-1 overflow-y-auto min-w-0 p-4 md:p-6 scroll-smooth bg-slate-50/50">

          <Box className="mb-4 md:mb-6">
            <Box className="flex items-center justify-center relative mb-4 md:mb-6">
              <Box className="text-center">
                <Typography variant="h5" component="h1" className="font-bold text-gray-800 text-lg md:text-3xl tracking-tight">
                  Stock Report
                </Typography>
              </Box>
            </Box>

            <Box className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 bg-white p-2 md:p-3 rounded-xl md:rounded-2xl shadow-sm border border-gray-200">
              <Box className="flex flex-col sm:flex-row items-center gap-3 w-full lg:max-w-2xl">
                <TextField
                  select
                  label="Department"
                  value={departmentFilter}
                  onChange={handleDepartmentChange}
                  size="small"
                  variant="outlined"
                  className="w-full sm:w-50 bg-gray-50/50 rounded-lg"
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="">All Departments</option>
                  {departmentOptions.map((dept, idx) => (
                    <option key={idx} value={dept}>{dept}</option>
                  ))}
                </TextField>

                <TextField
                  variant="outlined"
                  placeholder="Search GROUPNAME, BRAND, DESIGN..."
                  size="small"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  className="w-full bg-gray-50/50 rounded-lg"
                />
              </Box>

              <Box className="flex flex-row items-center justify-between lg:justify-end gap-4 w-full lg:w-auto">
                <Box className="text-left lg:text-right">
                  <Typography variant="caption" className="text-gray-400 uppercase font-bold tracking-wider text-[10px]">
                    Grand Total
                  </Typography>
                  <Typography variant="subtitle1" className="font-bold leading-tight text-blue-600">
                    Rs {grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>

                <Box>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={(e) => {
                      buttonRef.current = e.currentTarget;
                      setShowDropdown(!showDropdown);
                    }}
                    endIcon={<ArrowDownward />}
                    size="small"
                    className="rounded-lg px-4 py-1.5"
                  >
                    Export
                  </Button>
                  <Menu
                    anchorEl={buttonRef.current}
                    open={showDropdown}
                    onClose={() => setShowDropdown(false)}
                    PaperProps={{
                      className: "mt-1 shadow-xl border border-gray-100 rounded-xl"
                    }}
                  >
                    <MenuItem onClick={() => handleGenerateFile('PDF')} className="gap-3 py-2 px-4">
                      <PictureAsPdf className="text-red-500" />
                      <Typography variant="body2" className="font-medium">PDF</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => handleGenerateFile('Excel')} className="gap-3 py-2 px-4">
                      <TableView className="text-green-600" />
                      <Typography variant="body2" className="font-medium">Excel</Typography>
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>
            </Box>
          </Box>

          {loading && (
            <Box className="flex flex-col items-center justify-center py-20">
              <Lottie animationData={loader} loop={true} autoplay={true} style={{ height: 120, width: 120 }} />
              <Typography className="mt-4 text-slate-500 font-medium">Loading stock data...</Typography>
            </Box>
          )}

          {error && (
            <Box className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg mb-6">
              <Typography className="font-medium">Error</Typography>
              <Typography>{error}</Typography>
            </Box>
          )}

          {!loading && !error && filteredData.length > 0 && (
            <Paper elevation={1} className="w-full rounded-xl border border-gray-300 overflow-hidden shrink-0">
              <Box className="overflow-x-auto w-full" sx={{ WebkitOverflowScrolling: 'touch' }}>
                {(() => {
                  const activeMainHeaders = mainRowHeaders.filter(header => getTableHeaders.includes(header));
                  const gridTemplate = activeMainHeaders.map(h => {
                    if (h === 'DEPARTMENT') return 'minmax(180px, 1.5fr)';
                    if (h === 'GROUPNAME') return 'minmax(150px, 1fr)';
                    if (h === 'BRAND') return 'minmax(120px, 1fr)';
                    if (h === 'Grand Total') return 'minmax(120px, 1fr)';
                    return 'minmax(100px, 1fr)';
                  }).join(' ');
                  const minWidthValue = activeMainHeaders.reduce((acc, h) => {
                    if (h === 'DEPARTMENT') return acc + 180;
                    if (h === 'GROUPNAME') return acc + 150;
                    if (h === 'BRAND') return acc + 120;
                    if (h === 'Grand Total') return acc + 120;
                    return acc + 100;
                  }, 0);

                  return (
                    <Box style={{ minWidth: `${minWidthValue}px`, width: '100%' }}>
                      <Box
                        className="bg-blue-50 p-2 border-b border-gray-300 grid items-center"
                        style={{ gridTemplateColumns: gridTemplate }}
                      >
                        {activeMainHeaders.map((header, index) => (
                          <Box
                            key={index}
                            className={`flex items-center cursor-pointer text-blue-800 font-bold text-xs md:text-sm hover:text-blue-600 transition-colors ${header === 'Grand Total' ? 'justify-end pr-2' : ''}`}
                            onClick={() => handleSort(header)}
                          >
                            {header === 'DEPARTMENT' ? 'Department' : header}
                            {sortConfig.key === header ? (
                              sortConfig.direction === 'ascending' ? <ArrowUpward fontSize="inherit" className="ml-1" /> : <ArrowDownward fontSize="inherit" className="ml-1" />
                            ) : <Sort fontSize="inherit" className="ml-1 opacity-30" />}
                          </Box>
                        ))}
                      </Box>

                      <Box>
                        {currentPageData.map((row, rowIndex) => {
                          const isExpanded = !!expandedRows[rowIndex];

                          return (
                            <Box key={rowIndex} className="border-b border-gray-300 last:border-b-0">
                              <Box
                                className="p-1.5 grid items-center cursor-pointer hover:bg-gray-50/50 transition-colors"
                                style={{ gridTemplateColumns: gridTemplate }}
                                onClick={() => toggleRowExpansion(rowIndex)}
                              >
                                {activeMainHeaders.map((header, idx) => {
                                  if (header === 'DEPARTMENT') {
                                    return (
                                      <Box key={idx} className="flex items-center gap-2 min-w-0">
                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleRowExpansion(rowIndex); }} className="bg-gray-100 hover:bg-gray-200 p-1">
                                          {isExpanded ? <Remove fontSize="inherit" /> : <Add fontSize="inherit" />}
                                        </IconButton>
                                        <Typography variant="body2" className="font-semibold text-gray-700 truncate">
                                          {row[header]}
                                        </Typography>
                                      </Box>
                                    );
                                  }
                                  if (header === 'Grand Total') {
                                    return (
                                      <Typography
                                        key={idx}
                                        variant="body2"
                                        className="font-bold whitespace-nowrap text-blue-600 text-right pr-2"
                                      >
                                        {parseFloat(row[header] || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                      </Typography>
                                    );
                                  }
                                  return (
                                    <Typography key={idx} variant="body2" className="text-gray-600 truncate px-1">
                                      {row[header] !== null && row[header] !== '-' ? row[header] : '-'}
                                    </Typography>
                                  );
                                })}
                              </Box>

                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box className="p-4 bg-gray-50/50" style={{ minWidth: `${minWidthValue}px` }}>
                                  <Box className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                    {getTableHeaders.map((header, colIndex) => {
                                      if (!mainRowHeaders.includes(header) && row[header] !== null && row[header] !== '-') {
                                        return (
                                          <Box key={colIndex} className="bg-white p-2 rounded border border-gray-300 shadow-sm">
                                            <Typography variant="caption" className="text-gray-400 block uppercase font-bold text-[10px]">
                                              {header}
                                            </Typography>
                                            <Typography variant="body2" className="text-gray-700 font-medium">
                                              {row[header]}
                                            </Typography>
                                          </Box>
                                        );
                                      }
                                      return null;
                                    })}
                                  </Box>
                                </Box>
                              </Collapse>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            </Paper>
          )}

          {!loading && !error && filteredData.length === 0 && (
            <Box className="text-center py-20 bg-white rounded-xl border border-slate-200">
              <Typography className="text-slate-400">No data available for the selected criteria.</Typography>
            </Box>
          )}

          <Box className="mt-8 flex items-center justify-center gap-4">
            <Button
              variant="text"
              size="small"
              onClick={() => setPaginationInfo((prev) => ({ ...prev, currentPage: 1 }))}
              disabled={paginationInfo.currentPage === 1}
              className="text-blue-600 font-bold"
            >
              Start
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handlePageChange('prev')}
              disabled={paginationInfo.currentPage === 1}
            >
              &#8592;
            </Button>
            <Typography variant="body2" className="text-slate-600 font-medium">
              Page {paginationInfo.currentPage} of {totalPages}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handlePageChange('next')}
              disabled={paginationInfo.currentPage === totalPages}
            >
              &#8594;
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => setPaginationInfo((prev) => ({ ...prev, currentPage: totalPages }))}
              disabled={paginationInfo.currentPage === totalPages}
              className="text-blue-600 font-bold"
            >
              End
            </Button>
          </Box>
        </Box>
      </Box >
    </Box >
  );
};

export default Stock;
